import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export default class GlobalErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info });
    // eslint-disable-next-line no-console
    console.error("[GlobalErrorBoundary]", error, info);
  }

  reset = () => this.setState({ error: null, info: null });

  copy = async () => {
    const payload = {
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.info?.componentStack,
      route: window.location.pathname + window.location.search,
      ts: new Date().toISOString(),
      ua: navigator.userAgent,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      toast.success("Diagnostics copied to clipboard");
    } catch {
      toast.error("Could not copy diagnostics");
    }
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-xl border border-border bg-card p-6 text-center space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Something went wrong</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              An unexpected error interrupted this view. You can try again, or
              copy diagnostics for support.
            </p>
          </div>
          {this.state.error?.message && (
            <pre className="text-left text-xs bg-muted/40 rounded-md p-3 max-h-40 overflow-auto text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex justify-center gap-2">
            <Button onClick={this.reset} size="sm">
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
              Try again
            </Button>
            <Button onClick={this.copy} size="sm" variant="outline">
              <Copy className="mr-2 h-3.5 w-3.5" />
              Copy diagnostics
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
